import { useState, type RefObject } from 'react';
import { classifyType, isNumericClass } from '@/lib/values';
import type { ColumnSchema } from '@/types/api';
import type { FilterOperator, FilterSpec, SortDirection } from '@/cache/types';
import { Popover } from '@/components/common/Popover';

const TEXT_OPERATORS: readonly { value: FilterOperator; label: string }[] = [
  { value: 'contains', label: 'contains' },
  { value: 'notContains', label: 'does not contain' },
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'endsWith', label: 'ends with' },
  { value: 'isNull', label: 'is NULL' },
  { value: 'isNotNull', label: 'is not NULL' },
  { value: 'isEmpty', label: 'is empty string' },
];

const NUMBER_OPERATORS: readonly { value: FilterOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'between', label: 'between' },
  { value: 'isNull', label: 'is NULL' },
  { value: 'isNotNull', label: 'is not NULL' },
];

function needsValue(operator: FilterOperator): boolean {
  return operator !== 'isNull' && operator !== 'isNotNull' && operator !== 'isEmpty';
}

export interface ColumnMenuProps {
  readonly column: ColumnSchema;
  readonly sortDirection: SortDirection | null;
  readonly filter: FilterSpec | null;
  readonly onSort: (direction: SortDirection | null) => void;
  readonly onFilter: (filter: FilterSpec | null) => void;
  readonly onHide: () => void;
  readonly onClose: () => void;
  /** Opens the Filter & Sort panel seeded with this column. */
  readonly onFilterInPanel?: (() => void) | undefined;
  readonly anchorRef: RefObject<HTMLElement | null>;
}

export function ColumnMenu({
  column,
  sortDirection,
  filter,
  onSort,
  onFilter,
  onHide,
  onClose,
  onFilterInPanel,
  anchorRef,
}: ColumnMenuProps): JSX.Element {
  const numeric = isNumericClass(classifyType(column.type));
  const operators = numeric ? NUMBER_OPERATORS : TEXT_OPERATORS;

  const [operator, setOperator] = useState<FilterOperator>(
    filter?.operator ?? (numeric ? 'gte' : 'contains'),
  );
  const [first, setFirst] = useState(filter?.values[0] ?? '');
  const [second, setSecond] = useState(filter?.values[1] ?? '');

  const apply = (): void => {
    const values = operator === 'between' ? [first, second] : needsValue(operator) ? [first] : [];
    onFilter({ column: column.name, operator, values });
    onClose();
  };

  return (
    <Popover
      anchorRef={anchorRef}
      onClose={onClose}
      width={256}
      align="end"
      role="menu"
      ariaLabel={`Options for column ${column.name}`}
      testId="column-menu"
    >
    <div className="min-h-0 overflow-auto" onClick={(event) => event.stopPropagation()}>
      <div className="border-b border-line px-2.5 py-1.5">
        <div className="truncate font-mono text-xs text-ink">{column.name}</div>
        <div className="eyebrow mt-0.5">{column.typeText}</div>
      </div>

      <div className="flex gap-1 border-b border-line p-1.5">
        <button
          type="button"
          className="btn flex-1 justify-center"
          aria-pressed={sortDirection === 'asc'}
          onClick={() => {
            onSort(sortDirection === 'asc' ? null : 'asc');
            onClose();
          }}
        >
          Sort ↑
        </button>
        <button
          type="button"
          className="btn flex-1 justify-center"
          aria-pressed={sortDirection === 'desc'}
          onClick={() => {
            onSort(sortDirection === 'desc' ? null : 'desc');
            onClose();
          }}
        >
          Sort ↓
        </button>
      </div>

      <div className="space-y-1.5 border-b border-line p-1.5">
        <label className="eyebrow block" htmlFor={`op-${column.name}`}>
          Filter
        </label>
        <select
          id={`op-${column.name}`}
          className="field"
          value={operator}
          onChange={(event) => setOperator(event.target.value as FilterOperator)}
        >
          {operators.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>

        {needsValue(operator) ? (
          <input
            className="field"
            aria-label={`Filter value for ${column.name}`}
            value={first}
            placeholder="value"
            onChange={(event) => setFirst(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') apply();
            }}
          />
        ) : null}

        {operator === 'between' ? (
          <input
            className="field"
            aria-label={`Upper bound for ${column.name}`}
            value={second}
            placeholder="upper bound"
            onChange={(event) => setSecond(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') apply();
            }}
          />
        ) : null}

        <div className="flex gap-1">
          <button type="button" className="btn flex-1 justify-center btn-run" onClick={apply}>
            Apply
          </button>
          {filter ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                onFilter(null);
                onClose();
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {onFilterInPanel ? (
        <button
          type="button"
          className="w-full border-b border-line px-2.5 py-1.5 text-left text-xs text-muted transition-colors hover:bg-raised hover:text-ink"
          onClick={() => {
            onFilterInPanel();
            onClose();
          }}
        >
          Filter by this column…
        </button>
      ) : null}

      <button
        type="button"
        className="w-full px-2.5 py-1.5 text-left text-xs text-muted transition-colors hover:bg-raised hover:text-ink"
        onClick={() => {
          onHide();
          onClose();
        }}
      >
        Hide column
      </button>
    </div>
    </Popover>
  );
}
